import { useEditor } from '@craftjs/core';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as UndoIcon } from 'assets/img/formBuilder/undo.svg';
import clsx from 'clsx';
import styles from './UIEditorSaveResetControls.module.scss';
import { useCommonLocalization } from 'src/application/localisation/useCommonLocalization';
import { AppButton } from 'uikit/buttons';
import { AppButtonWithMenu } from 'uikit/buttons/buttonWithMenu/buttonWithMenu.component';
import { DropDownMenuOption } from 'uikit/dropDownMenu/dropDownMenu';
import { conditionalConcat } from 'src/helpers/arrayHelpers';
import useLayoutExport from '../importLayout/useLayoutImportExport';

type Props = {
  disabled: boolean;
  isShowControls: boolean;
  canDelete?: boolean;
  exportFileName?: string;
  onPreviewOpen?: () => void;
  save: () => Promise<void>;
  saveAs?: () => Promise<void>;
  delete?: () => Promise<void>;
  downloadImages?: () => Promise<void>;
};

export const UIEditorSaveResetControls: React.FC<Props> = (props) => {
  const { actions, query, canUndo, canRedo } = useEditor((state, queryParam) => ({
    enabled: state.options.enabled,
    canUndo: queryParam.history.canUndo(),
    canRedo: queryParam.history.canRedo(),
  }));

  const { t } = useTranslation('dev');
  const commonLocalizer = useCommonLocalization();
  const { exportLayout } = useLayoutExport();

  const options = useMemo(
    () =>
      conditionalConcat<DropDownMenuOption>(
        !!props.saveAs && [
          {
            key: 0,
            text: commonLocalizer('Common_SaveAs'),
            action: props.saveAs,
          },
        ],
        [
          {
            key: 1,
            text: t('StudySettingsPage.FormBuilder.copy_to_clipboard'),
            action: () => {
              const json = query.serialize();
              navigator.clipboard.writeText(json);
            },
          },
          {
            key: 3,
            text: t('StudySettingsPage.FormBuilder.export_layout'),
            action: () => exportLayout(props.exportFileName),
          },
        ],
        !!props.downloadImages &&
          ([
            {
              key: 2,
              text: t('StudySettingsPage.FormBuilder.DownloadImages'),
              action: props.downloadImages,
            },
          ] satisfies DropDownMenuOption[]),
      ),
    [commonLocalizer, exportLayout, props.downloadImages, props.exportFileName, props.saveAs, query, t],
  );

  const [deleting, setDeleting] = useState<boolean>(false);
  const onDelete = useCallback(async () => {
    setDeleting(true);
    await props.delete?.();
    setDeleting(false);
  }, [props]);

  const [saving, setSaving] = useState<boolean>(false);
  const onSave = useCallback(async () => {
    setSaving(true);
    await props.save();
    setSaving(false);
  }, [props]);

  if (!props.isShowControls) {
    return null;
  }

  return (
    <div className={styles.controls}>
      <AppButton
        variant={'icon-link'}
        colorSchema={'primary'}
        onClick={() => actions.history.undo()}
        disabled={props.disabled || !canUndo || saving}
        Icon={UndoIcon}
        className={styles.button}
      />
      <AppButton
        variant={'icon-link'}
        colorSchema={'primary'}
        onClick={() => actions.history.redo()}
        disabled={props.disabled || !canRedo || saving}
        Icon={UndoIcon}
        className={clsx(styles.button, styles.redo)}
      />
      <AppButton
        variant={'icon-link'}
        colorSchema={'primary'}
        onClick={props.onPreviewOpen}
        text={t('StudySettingsPage.FormBuilder.preview')}
        className={styles.button}
        disabled={props.disabled || !props.onPreviewOpen || saving}
      />
      <AppButtonWithMenu
        options={options}
        buttonText={commonLocalizer('Common_Save')}
        onClick={onSave}
        className={styles.saveButton}
        disabled={props.disabled}
        isLoading={props.disabled || saving}
      />
      <AppButton
        variant={'button'}
        colorSchema={'destroy'}
        text={commonLocalizer('Common_Delete')}
        onClick={onDelete}
        disabled={props.disabled || !props.canDelete || deleting || saving}
        isLoading={deleting}
      />
    </div>
  );
};
